'use client';

import Icon from '@/components/ui/AppIcon';

interface IntellectualPropertySectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function IntellectualPropertySection({ isExpanded, onToggle }: IntellectualPropertySectionProps) {
  const ipCategories = [
    {
      title: 'Website Content',
      items: [
        'All text, images, graphics, and multimedia content are proprietary',
        'Proximus logo and branding elements are registered trademarks',
        'Unauthorized reproduction or distribution strictly prohibited',
        'Content may not be used for commercial purposes without written consent'
      ]
    },
    {
      title: 'Proprietary Systems',
      items: [
        'Booking platform and reservation system are proprietary technology',
        'Software, algorithms, and databases protected under copyright law',
        'Reverse engineering or unauthorized access is illegal',
        'API access requires separate licensing agreement'
      ]
    },
    {
      title: 'Guest Data Usage Rights',
      items: [
        'Guest reviews and testimonials may be used for marketing purposes',
        'Photos taken on property may be used in promotional materials',
        'Personal data handled according to Privacy Policy',
        'Guests may request removal of their content at any time'
      ]
    }
  ];

  return (
    <section id="intellectual-property" className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="LockClosedIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Section 4</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Intellectual Property
            </h2>
            <p className="text-lg text-text-secondary">
              Protections covering website content, proprietary systems, and guest data usage rights.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-premium border border-border overflow-hidden">
            <button
              onClick={onToggle}
              className="w-full px-6 py-6 flex items-center justify-between hover:bg-muted transition-colors duration-300"
            >
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="DocumentDuplicateIcon" size={24} className="text-primary" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-semibold text-text-primary">View Intellectual Property Rights</h3>
                  <p className="text-sm text-text-secondary mt-1">Click to expand IP protections and usage rights</p>
                </div>
              </div>
              <Icon 
                name={isExpanded ? "ChevronUpIcon" : "ChevronDownIcon"} 
                size={24} 
                className="text-text-secondary flex-shrink-0" 
              />
            </button>

            {isExpanded && (
              <div className="px-6 pb-6 space-y-6 animate-scale-in">
                {ipCategories.map((category, index) => (
                  <div key={index} className="border-t border-border pt-6">
                    <h4 className="text-lg font-semibold text-text-primary mb-4">{category.title}</h4>
                    <ul className="space-y-3">
                      {category.items.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon name="CheckCircleIcon" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                          <span className="text-text-secondary">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="border-t border-border pt-6">
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                    <div className="flex items-start space-x-3">
                      <Icon name="ExclamationTriangleIcon" size={24} className="text-primary flex-shrink-0" />
                      <div>
                        <h5 className="font-semibold text-text-primary mb-2">Copyright Notice</h5>
                        <p className="text-sm text-text-secondary mb-3">
                          © 2026 Proximus Serviced Apartments. All rights reserved. Unauthorized use, reproduction, or distribution of any content from this website or property is strictly prohibited and may result in legal action.
                        </p>
                        <p className="text-sm text-text-secondary">
                          For licensing inquiries or permission requests, please contact our legal department at legal@proximusapartments.com
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}